import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Circle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const PartyingTonightPage = () => {
  const navigate = useNavigate();
  const [checkedItems, setCheckedItems] = useState({});

  const toggleCheck = (id) => {
    setCheckedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const sections = [
    {
      title: '🧠 Check In With Yourself',
      subtitle: 'Before you go out, pause for a second.',
      items: [
        { id: 'mood', text: 'How am I feeling?' },
        { id: 'energy', text: 'What\'s my energy like?' },
        { id: 'want', text: 'What do I want from tonight?' }
      ]
    },
    {
      title: '📋 Plan Ahead',
      subtitle: 'A little prep helps you enjoy the night more.',
      items: [
        { id: 'substance', text: 'Know what and how much you\'ll use' },
        { id: 'mixing', text: 'Be mindful of mixing drugs and meds' },
        { id: 'test', text: 'Check combinations and test your supply if you can' },
        { id: 'home', text: 'Plan how you\'re getting home safely' }
      ]
    },
    {
      title: '🤝 Look After Each Other',
      subtitle: 'Partying is better (and safer) together.',
      items: [
        { id: 'boundaries', text: 'Talk with buddies about boundaries, consent, and plans' },
        { id: 'checkin', text: 'Check in on each other — remind folks to eat, drink water, or take a break' },
        { id: 'stepping', text: 'Let someone know if you\'re stepping away' },
        { id: 'alone', text: 'Don\'t party alone' }
      ]
    },
    {
      title: '💜 Aftercare Matters',
      subtitle: 'Taking care of yourself keeps the vibe good.',
      items: [
        { id: 'recover', text: 'Give yourself time to recover' },
        { id: 'eat', text: 'Eat, hydrate, and sleep' },
        { id: 'calm', text: 'Do something calming' },
        { id: 'connected', text: 'Stay connected — don\'t isolate' },
        { id: 'support', text: 'Reach out for support if you\'re feeling low' }
      ]
    }
  ];

  const totalItems = sections.reduce((acc, s) => acc + s.items.length, 0);
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const progress = (checkedCount / totalItems) * 100;

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">🥳</span>
              <h1 className="text-2xl font-bold text-gradient">
                Partying Tonight?
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {/* Hero */}
        <div className="mb-6 p-6 party-gradient rounded-3xl text-white text-center">
          <div className="text-5xl mb-3">✨</div>
          <h2 className="text-2xl font-bold mb-3">Quick Party Checklist</h2>
          <p className="text-white/90 leading-relaxed">
            Quick mental health tips for a safer, better night. Tap to check off! 💜
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6 p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[rgb(var(--text-primary))]">Your Prep Progress</span>
            <span className="text-sm text-[rgb(var(--text-secondary))]">{checkedCount}/{totalItems}</span>
          </div>
          <div className="h-3 bg-[rgb(var(--background))] rounded-full overflow-hidden">
            <div 
              className="h-full party-gradient transition-all duration-500 ease-out rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
          {checkedCount === totalItems && (
            <p className="text-sm text-[rgb(var(--success))] mt-2 text-center font-semibold">
              🎉 You're all set! Have a great night!
            </p>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, sectionIdx) => (
            <div key={sectionIdx} className="bg-[rgb(var(--surface))] rounded-2xl p-5 border border-white/5">
              <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-1">
                {section.title}
              </h3>
              <p className="text-sm text-[rgb(var(--text-secondary))] mb-4">
                {section.subtitle}
              </p>
              <div className="space-y-3">
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => toggleCheck(item.id)}
                    data-testid={`check-${item.id}`}
                    className={`w-full flex items-center p-3 rounded-xl transition-all ${
                      checkedItems[item.id] 
                        ? 'bg-[rgb(var(--success))]/10 border border-[rgb(var(--success))]/30' 
                        : 'bg-[rgb(var(--background))] border border-white/5 hover:border-[rgb(var(--primary))]/30'
                    }`}
                  >
                    {checkedItems[item.id] ? (
                      <CheckCircle size={24} weight="duotone" className="text-[rgb(var(--success))] mr-3 flex-shrink-0" />
                    ) : (
                      <Circle size={24} weight="duotone" className="text-[rgb(var(--text-secondary))] mr-3 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${checkedItems[item.id] ? 'text-[rgb(var(--text-primary))] line-through opacity-70' : 'text-[rgb(var(--text-secondary))]'}`}>
                      {item.text}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
          <p className="text-[rgb(var(--text-primary))] font-bold mb-2">
            Taking care of yourself and each other keeps the vibe good — before, during, and after. 💜
          </p>
          <p className="text-xs text-[rgb(var(--text-secondary))] mt-2">
            Inspired by ACT - Partying and Mental Health
          </p>
        </div>
      </main>
    </div>
  );
};

export default PartyingTonightPage;
